import CurrencyContext from "./CurrencyContext";
import { useEffect, useReducer, useContext } from "react";
import ErrorMessageContext from './ErrorMessageContext';

// Constants
const DEFAULT_CURRENCY = "USD";
const DEFAULT_LANGUAGE = "en";
const LANGUAGES = ['fr', 'en'];
const CURRENCY_SYMBOL_JSON = "api/world_currency_symbols.json";

// Helper functions
const getUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    currency: urlParams.get('currency')?.toUpperCase() || null,
    language: urlParams.get('language')?.toLowerCase() || DEFAULT_LANGUAGE,
  };
};

const urlParams = getUrlParams();

const initialCurrencyState = {
  isLoaded: true,
  error: null,
  loadedCurrencies: {
    'USD': { Symbol: '$', Locale: 'en-US' },
  },
  conversionRates: {
    'USD': 1,
  },
  languageParentWindow: urlParams.language,
  selectedCurrency: urlParams.currency || 'USD',
  currentConversionRate: 1,
  locale: 'en-US',  
  isInitialized: false
};

// Reducer
const currencyReducer = (state, action) => {
  switch (action.type) {
    case 'CHANGE_CURRENCY':
      return {
        ...state,
        selectedCurrency: action.currency,
        currentConversionRate: state.conversionRates[action.currency],
        locale: state.loadedCurrencies && 
                action.currency && 
                state.loadedCurrencies[action.currency] ? 
                  state.loadedCurrencies[action.currency].Locale || 'en-US' : 'en-US'
      };
    case 'CHANGE_LANGUAGE':
      return {
        ...state,
        languageParentWindow: action.language
      };
    case 'INIT':
      return {
        ...state,
        isLoaded: true,
        isInitialized: true,
        loadedCurrencies: action.loadedCurrencies,
        conversionRates: action.conversionRates,
        selectedCurrency: action.selectedCurrency,
        currentConversionRate: action.currentConversionRate,
        languageParentWindow: action.languageParentWindow,
        locale: action.loadedCurrencies && 
                action.selectedCurrency && 
                action.loadedCurrencies[action.selectedCurrency] ? 
                action.loadedCurrencies[action.selectedCurrency].Locale || 'en-US' : 'en-US'
      };
    case 'ERROR':
      return { ...state, error: action.error, isLoaded: true };
    default:
      return state;
  }
};

const CurrencyProvider = ({ children }) => {
  const errorMessageCtx = useContext(ErrorMessageContext);
  const [currencyState, dispatchCurrencyAction] = useReducer(currencyReducer, initialCurrencyState);

  const changeCurrencyHandler = (currency) => {
    dispatchCurrencyAction({ type: "CHANGE_CURRENCY", currency });
  };

  const changeLanguageHandler = (language) => {
    dispatchCurrencyAction({ type: "CHANGE_LANGUAGE", language });
  };

  const currencyContext = {
    ...currencyState,
    changeCurrency: changeCurrencyHandler,
    changeLanguage: changeLanguageHandler,
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const fetchOptions = {
          headers: { 'Accept': 'application/json' },
          redirect: 'follow',
        };
  
        let detectedCurrency = null;
  
        try {
          const geoResponse = await fetch('https://www.planhub.ca/ip-information?dyn=1', fetchOptions);
          const geoData = await geoResponse.json();
          
          if (geoData && geoData.country_code) {
            
            try {
              const countryResponse = await fetch('/api/countryAPI.json', fetchOptions);
              const countriesData = await countryResponse.json();
              
              const country = countriesData.find(c => c.cca2 === geoData.country_code);
              
              if (country && country.currencies) {
                const currencyCode = Object.keys(country.currencies)[0];
                if (currencyCode) {
                  detectedCurrency = currencyCode;
                  console.log(`Devise trouvée dans countryAPI.json: ${currencyCode}`);
                }
              }
            } catch (countryError) {
              console.error("Erreur lors de la récupération des données pays:", countryError);
            }
            
            if (!detectedCurrency) {
              const commonCurrencies = {
                'CA': 'CAD',
                'US': 'USD',
                'GB': 'GBP',
                'AU': 'AUD',
                'EU': 'EUR',
                'FR': 'EUR',
                'DE': 'EUR',
                'JP': 'JPY'
              };
              
              detectedCurrency = commonCurrencies[geoData.country_code] || DEFAULT_CURRENCY;
            }
          }
        } catch (geoError) {
          console.error("Erreur lors de la détection du pays:", geoError);
        }
  
        const [exchangeRateResponse, currencySymbolsResponse] = await Promise.all([
          fetch(process.env.REACT_APP_EXCHANGE_RATE, fetchOptions),
          fetch(CURRENCY_SYMBOL_JSON, fetchOptions)
        ]);
  
        const [exchangeRateData, currencySymbolsData] = await Promise.all([
          exchangeRateResponse.json(),
          currencySymbolsResponse.json()
        ]);
  
        const loadedCurrencies = Object.fromEntries(
          Object.entries(currencySymbolsData).map(([key, value]) => [value.Code, { Symbol: value.Symbol, Locale: value.Locale }])
        );
  
        const conversionRates = exchangeRateData.conversion_rates;
        
        let currency = detectedCurrency || DEFAULT_CURRENCY;
        console.log("Devise forcée par détection IP:", currency);
                
        if (!conversionRates[currency] || !loadedCurrencies[currency]) {
          currency = DEFAULT_CURRENCY;
          
          if (!conversionRates[currency] || !loadedCurrencies[currency]) {
            const availableCurrencies = Object.keys(conversionRates).filter(code => loadedCurrencies[code]);
            if (availableCurrencies.length > 0) {
              currency = availableCurrencies[0];
            } else {
              console.error("Aucune devise valide trouvée!");
            }
          }
        }
        
        let language = currencyState.languageParentWindow;
  
        if (!LANGUAGES.includes(language)) {
          errorMessageCtx.addErrorMessage(`"${language}" non reconnu pour data-language, remplacé par "${DEFAULT_LANGUAGE}"`);
          language = DEFAULT_LANGUAGE;
        }
        
        if (!conversionRates[currency]) {
          const errorMessage = language === 'fr'
            ? `"${currency}" non reconnu pour data-currency, remplacé par "${DEFAULT_CURRENCY}"`
            : `"${currency}" not recognized for data-currency, replaced with "${DEFAULT_CURRENCY}"`;
          errorMessageCtx.addErrorMessage(errorMessage);
          currency = DEFAULT_CURRENCY;
        }
  
        dispatchCurrencyAction({
          type: "INIT",
          loadedCurrencies,
          conversionRates,
          selectedCurrency: currency,
          currentConversionRate: conversionRates[currency],
          languageParentWindow: language
        });
      } catch (error) {
        dispatchCurrencyAction({ type: "ERROR", error });
      }
    };
  
    if (!currencyState.isInitialized) {
      fetchData();
    }    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorMessageCtx]);

  return (
    <CurrencyContext.Provider value={currencyContext}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyProvider;